import { EXPORT_THEME } from '../theme';
import {
  BLOCK_WIDTH,
  GAP_COLS,
  buildSideBySideMatrix,
  buildStoreBlocks,
  getEventExportBasename,
} from '../eventPlanData';
import {
  addBrandedHeader,
  applyAutoColumnWidths,
  buildExportSubtitle,
  createStyledWorkbook,
  downloadWorkbook,
  getBodyStyle,
  getHeaderStyle,
  getThinBorder,
} from './helpers';

function getStoreTitleStyle() {
  return {
    font: { bold: true, color: { argb: EXPORT_THEME.white }, size: 11 },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: EXPORT_THEME.brandOrange } },
    alignment: { vertical: 'middle', horizontal: 'center', wrapText: true },
    border: getThinBorder(),
  };
}

function getMetaStyle() {
  return {
    font: { italic: true, color: { argb: EXPORT_THEME.mutedText }, size: 9 },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: EXPORT_THEME.stripeFill } },
    alignment: { vertical: 'top', horizontal: 'left', wrapText: true },
    border: getThinBorder(),
  };
}

function styleEventPlanCells(worksheet, matrix, layout, offsetRow) {
  const { startRow, headerRowIdx, dataStartIdx } = layout;

  matrix.forEach((rowValues, rowIndex) => {
    rowValues.forEach((value, colIndex) => {
      if (value === '' || value == null) return;

      const excelRow = offsetRow + rowIndex + 1;
      const excelCol = colIndex + 1;
      const cell = worksheet.getCell(excelRow, excelCol);
      cell.value = value;

      if (rowIndex === startRow) {
        cell.style = getStoreTitleStyle();
        return;
      }

      if (rowIndex > startRow && rowIndex < headerRowIdx) {
        cell.style = getMetaStyle();
        return;
      }

      if (rowIndex === headerRowIdx) {
        cell.style = getHeaderStyle();
        return;
      }

      if (rowIndex >= dataStartIdx) {
        const stripe = (rowIndex - dataStartIdx) % 2 === 1;
        cell.style = getBodyStyle(stripe);
      }
    });
  });
}

export async function exportEventToExcel(event, participants) {
  const blocks = buildStoreBlocks(event, participants);
  const { workbook, worksheet } = await createStyledWorkbook('Turni');

  const dataStartRow = await addBrandedHeader(worksheet, workbook, {
    title: event.name || 'Piano turni',
    subtitle: buildExportSubtitle(
      `${blocks.length} negozi · ${event.slots?.length || 0} fasce orarie`
    ),
    totalColumns: Math.max(blocks.length * BLOCK_WIDTH - GAP_COLS, 4),
  });

  const matrixStartRow = dataStartRow;
  if (blocks.length === 0) {
    const cell = worksheet.getCell(matrixStartRow, 1);
    cell.value = 'Nessun negozio configurato.';
    cell.style = getBodyStyle();
    await downloadWorkbook(workbook, `${getEventExportBasename(event)}.xlsx`);
    return;
  }

  const { matrix, merges, layout } = buildSideBySideMatrix(blocks, 0);

  matrix.forEach((rowValues, rowIndex) => {
    rowValues.forEach((value, colIndex) => {
      if (value === '' || value == null) return;
      worksheet.getCell(matrixStartRow + rowIndex, colIndex + 1).value = value;
    });
  });

  merges.forEach(({ r0, c0, r1, c1 }) => {
    worksheet.mergeCells(
      matrixStartRow + r0 + 1,
      c0 + 1,
      matrixStartRow + r1 + 1,
      c1 + 1
    );
  });

  styleEventPlanCells(worksheet, matrix, layout, matrixStartRow - 1);

  if (matrix[0]?.length) {
    applyAutoColumnWidths(worksheet, {
      fromRow: matrixStartRow + layout.headerRowIdx,
      fromCol: 1,
      toCol: matrix[0].length,
      minWidth: 14,
      maxWidth: 42,
    });

    matrix[0].forEach((_, colIndex) => {
      if ((colIndex + 1) % BLOCK_WIDTH === 0) {
        worksheet.getColumn(colIndex + 1).width = 3;
      }
    });
  }

  await downloadWorkbook(workbook, `${getEventExportBasename(event)}.xlsx`);
}
